import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { Child } from '../lib/types';
import { useParent } from './ParentContext';

/**
 * Reading the child's school site (#654), starting with Connexus, done by the child's own computer (#673): its
 * ClassGuardian browser extension reads Connexus with the child's student sign-in when they open it, in the background
 * every few hours, and when a parent presses Update now here. Nothing runs in the parent's browser.
 */
export interface SchoolLinkInfo {
  available: boolean;
  link: {
    provider: 'connexus' | '';
    planner: boolean;
    grades: boolean;
    /** Fill attendance from the lessons the child finished (#673). */
    attendance?: boolean;
    studentId: string;
    studentName: string;
    lastPlanner: number;
    lastGrades: number;
    lastAttendance?: number;
    /** A parent's Update now waiting for the child's computer (#669), and why its last try failed. */
    requestedAt?: number;
    requestError?: string;
  };
  courses: { id: string; name: string; score: string; grade: string; completion: string; teacher: string; updatedAt: number }[];
  /** How often the child's computer reads in the background, in hours (Admin); 0 = only on open / Update now. */
  everyHours?: number;
  /** The child's browser extension: paired, and checked in within the last few minutes (#669). */
  childBrowser?: { paired: boolean; online: boolean };
}

export function useSchoolLink(childId: string) {
  const [info, setInfo] = useState<SchoolLinkInfo | null>(null);
  const load = useCallback(() => {
    api
      .get<SchoolLinkInfo>(`/api/children/${childId}/school-link`)
      .then(setInfo)
      .catch(() => setInfo(null));
  }, [childId]);
  useEffect(load, [load]);
  return { info, setInfo, reload: load };
}

const when = (ms: number) => {
  const d = new Date(ms);
  const today = d.toDateString() === new Date().toDateString();
  return `${today ? 'today' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};

/**
 * The line above the Planner, Grade Book or Attendance saying where its Connexus items came from and when, with Update
 * now: it asks the child's computer to read Connexus, and the line updates by itself when that lands.
 */
export function SchoolBanner({
  child,
  info,
  part,
  onSettings,
  onUpdated,
}: {
  child: Child;
  info: SchoolLinkInfo | null;
  part: 'planner' | 'grades' | 'attendance';
  onSettings?: () => void;
  onUpdated?: (i: SchoolLinkInfo) => void;
}) {
  const { toast } = useParent();
  const [problem, setProblem] = useState('');
  const waiting = !!info?.link.requestedAt;
  // While Update now waits for the child's computer, look again every few seconds and update when it answers.
  useEffect(() => {
    if (!waiting) return;
    let live = true;
    const t = setInterval(() => {
      api
        .get<SchoolLinkInfo>(`/api/children/${child.id}/school-link`)
        .then((next) => {
          if (!live || next.link.requestedAt) return;
          onUpdated?.(next);
          if (!next.link.requestError) toast({ text: `Updated ${child.name} from Connexus.`, tone: 'good' });
        })
        .catch(() => undefined);
    }, 5000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [waiting, child.id, child.name, onUpdated, toast]);
  if (!info?.available || info.link.provider !== 'connexus' || !info.link[part]) return null;

  const last = (part === 'planner' ? info.link.lastPlanner : part === 'grades' ? info.link.lastGrades : info.link.lastAttendance) ?? 0;
  const computer = info.childBrowser;
  const every = info.everyHours ?? 0;
  const asked = waiting
    ? computer?.online
      ? `Asked ${child.name}’s computer to read Connexus… usually within a minute.`
      : computer?.paired
        ? `${child.name}’s computer is off. It’ll read Connexus when it’s back on (the request waits a day).`
        : `${child.name}’s computer doesn’t have the ClassGuardian browser extension yet, so it can’t read Connexus.`
    : '';
  const failed =
    !waiting && info.link.requestError
      ? info.link.requestError === 'signin'
        ? `${child.name}’s computer couldn’t read Connexus: ${child.name} isn’t signed in there. Have them sign in to Connexus, then press Try again.`
        : `${child.name}’s computer couldn’t read Connexus (${info.link.requestError}). Press Try again.`
      : '';
  const text =
    asked ||
    (last
      ? `From Connexus · read by ${child.name}’s computer ${when(last)}${every ? ` · next read about ${when(last + every * 3_600_000)}` : ''}`
      : `Connexus is on. It comes in from ${child.name}’s computer when they open Connexus${every ? `, every ${every} hour${every === 1 ? '' : 's'}` : ''}, or press Update now.`);

  async function update() {
    setProblem('');
    try {
      onUpdated?.(await api.post<SchoolLinkInfo>(`/api/children/${child.id}/school-sync/request`));
    } catch (e) {
      setProblem((e as Error).message);
    }
  }

  return (
    <div className="school-banner stack tight">
      <div className="row-between wrap">
        <span>
          <span className={`dot ${waiting ? 'waiting' : last && !failed ? 'on' : ''}`} /> {text}
        </span>
        <span className="row-gap">
          <button type="button" className="btn small" onClick={() => void update()} disabled={waiting}>
            {waiting ? 'Asked' : failed ? 'Try again' : 'Update now'}
          </button>
          {onSettings && (
            <button type="button" className="link-btn small" onClick={onSettings}>
              Connexus settings
            </button>
          )}
        </span>
      </div>
      {(problem || failed) && <p className="small warn-text">{problem || failed}</p>}
    </div>
  );
}

/** Turn reading Connexus on or off for a child, and choose the Planner, the grades and attendance. */
export function ConnexusSettings({ child, info, onSaved, onClose }: { child: Child; info: SchoolLinkInfo; onSaved: (i: SchoolLinkInfo) => void; onClose?: () => void }) {
  const { toast } = useParent();
  const on = info.link.provider === 'connexus';
  const [planner, setPlanner] = useState(on ? info.link.planner : true);
  const [grades, setGrades] = useState(on ? info.link.grades : true);
  const [attendance, setAttendance] = useState(on ? !!info.link.attendance : true);
  const [busy, setBusy] = useState(false);
  const every = info.everyHours ?? 0;

  async function save(provider: 'connexus' | '') {
    if (on && (!provider || (info.link.grades && !grades) || (info.link.planner && !planner) || (info.link.attendance && !attendance)) && !confirm('Turning this off removes what ClassGuardian read from Connexus. Turn it off?')) return;
    setBusy(true);
    try {
      const next = await api.put<SchoolLinkInfo>(`/api/children/${child.id}/school-link`, { provider, planner, grades, attendance });
      onSaved(next);
      toast({ text: provider ? `Connexus is on for ${child.name}.` : `Connexus is off for ${child.name}.`, tone: 'good' });
      onClose?.();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack sub-section">
      <p className="muted small">
        {child.name}’s computer reads their Connexus with their own sign-in: when they open it
        {every ? `, every ${every} hour${every === 1 ? '' : 's'} while their browser is open,` : ''} and when you press Update now. It needs the ClassGuardian
        browser extension on their computer. No Connexus password is stored.
      </p>
      <label className="check">
        <input type="checkbox" checked={planner} onChange={(e) => setPlanner(e.target.checked)} />
        <span>
          Read {child.name}’s Connexus Planner
          <span className="muted small block">Lessons and LiveLessons by day, ticked off when Connexus has them done</span>
        </span>
      </label>
      <label className="check">
        <input type="checkbox" checked={grades} onChange={(e) => setGrades(e.target.checked)} />
        <span>
          Read {child.name}’s Connexus grades
          <span className="muted small block">Course grades and every scored assessment, into the Grade Book</span>
        </span>
      </label>
      <label className="check">
        <input type="checkbox" checked={attendance} onChange={(e) => setAttendance(e.target.checked)} />
        <span>
          Fill attendance from the lessons {child.name} finishes
          <span className="muted small block">
            Connexus doesn’t show attendance to students, so a day {child.name} finished Connexus lessons counts as a school day of their scheduled hours. Your
            own entries always stay.
          </span>
        </span>
      </label>
      {on && info.link.studentName && <p className="muted small">Connexus student: {info.link.studentName}</p>}
      <div className="row-gap wrap">
        <button className="btn primary small" onClick={() => void save('connexus')} disabled={busy || (!planner && !grades && !attendance)}>
          {on ? 'Save' : 'Turn on'}
        </button>
        {on && (
          <button className="btn small danger-text" onClick={() => void save('')} disabled={busy}>
            Turn off
          </button>
        )}
        {onClose && (
          <button className="btn small" onClick={onClose}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

/** The family's Connexus attendance bookmark (#680), as the server describes it. */
interface AttendanceBookmark {
  available: boolean;
  from: string;
  /** Students a parent matched to children; they can be cleared to match again (#682). */
  matched?: number;
  bookmark: { createdAt: number; lastUsedAt: number; by: string; summary: { childId: string; name: string; days: number }[] } | null;
  /** Only right after making one: where the bookmark loads its script from (holds the key). */
  script?: string;
}

/** What the bookmark itself runs: load ClassGuardian's reader into the Connexus page it's clicked on. */
const bookmarkletFor = (script: string) =>
  `javascript:(()=>{const s=document.createElement('script');s.src=${JSON.stringify(script)}+'&t='+Date.now();document.body.appendChild(s)})()`;

/**
 * Connexus's own attendance (#680). Students can't see it, so it comes from a parent's Learning Coach sign-in: a
 * "Send to ClassGuardian" bookmark, clicked on Connexus's Attendance page in their own browser, sends each day's code
 * here. No extension and no Connexus password. Finished lessons (#673) only fill the days Connexus hasn't covered.
 */
export function ConnexusAttendanceCard({ child, onSent }: { child: Child; onSent?: () => void }) {
  const { toast } = useParent();
  const [state, setState] = useState<AttendanceBookmark | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const load = useCallback(() => {
    api
      .get<AttendanceBookmark>('/api/family/connexus-bookmark')
      .then((s) => setState((was) => ({ ...s, script: was?.script })))
      .catch(() => setState(null));
  }, []);
  useEffect(load, [load]);
  // React won't put a javascript: address in an href, so the draggable bookmark gets it directly.
  useEffect(() => {
    if (linkRef.current && state?.script) linkRef.current.setAttribute('href', bookmarkletFor(state.script));
  }, [state?.script, open]);
  // Coming back to the tab after a send on Connexus: look again, and show the new days.
  useEffect(() => {
    const again = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', again);
    return () => document.removeEventListener('visibilitychange', again);
  }, [load]);
  const lastUsed = state?.bookmark?.lastUsedAt ?? 0;
  useEffect(() => {
    if (lastUsed) onSent?.();
  }, [lastUsed, onSent]);
  if (!state?.available) return null;

  const b = state.bookmark;
  const mine = b?.summary.find((s) => s.childId === child.id);

  async function make() {
    if (b && !confirm('Make a new bookmark? The one you have now stops working.')) return;
    setBusy(true);
    try {
      setState(await api.post<AttendanceBookmark>('/api/family/connexus-bookmark'));
      setOpen(true);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  async function turnOff() {
    if (!confirm('Turn the bookmark off? It stops working until you make a new one. Attendance already sent stays.')) return;
    setState(await api.del<AttendanceBookmark>('/api/family/connexus-bookmark'));
  }
  async function rematch() {
    if (!confirm('Match the students again? The next time you click the bookmark, it asks which child each Connexus student is. Days already sent stay until then.')) return;
    setState(await api.del<AttendanceBookmark>('/api/family/connexus-bookmark/students'));
  }
  async function copy() {
    if (!state?.script) return;
    try {
      await navigator.clipboard.writeText(bookmarkletFor(state.script));
      toast({ text: 'Copied. Make a new bookmark and paste this as its address.', tone: 'good' });
    } catch {
      toast({ text: 'Couldn’t copy. Drag the button to your bookmarks bar instead.', tone: 'alert' });
    }
  }

  return (
    <div className="school-banner stack tight att-bookmark">
      <div className="row-between wrap">
        <span>
          <span className={`dot ${mine ? 'on' : ''}`} />{' '}
          {mine && b
            ? `From Connexus attendance · sent by ${b.by} ${when(b.lastUsedAt)}`
            : b?.lastUsedAt
              ? `Connexus attendance last sent ${when(b.lastUsedAt)} (none for ${child.name} yet)`
              : 'Bring in Connexus’s own attendance with a bookmark'}
        </span>
        <button type="button" className="link-btn small" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? 'Close' : b ? 'How to send it again' : 'Set it up'}
        </button>
      </div>
      {open && (
        <div className="stack tight">
          <p className="muted small">
            Students can’t see attendance in Connexus, so it comes from your Learning Coach sign-in, in your own browser. No extension, and no
            Connexus password stored. Connexus’s codes win on every day they cover, finished lessons fill the rest, and your own entries stay.
          </p>
          {state.script ? (
            <ol className="small att-steps">
              <li>
                Drag this button to your bookmarks bar:{' '}
                <a ref={linkRef} className="btn primary small" onClick={(e) => e.preventDefault()} draggable>
                  Send to ClassGuardian
                </a>{' '}
                <button type="button" className="link-btn small" onClick={() => void copy()}>
                  Copy it instead (iPad or phone)
                </button>
              </li>
              <li>In Connexus, open Attendance as the Learning Coach.</li>
              <li>Click the bookmark. Every child’s attendance comes in, as far back as Connexus has it.</li>
            </ol>
          ) : b ? (
            <p className="small">
              Open Attendance in Connexus as the Learning Coach and click your <strong>Send to ClassGuardian</strong> bookmark. Lost it? Make a new
              one.
            </p>
          ) : null}
          <div className="row-gap wrap">
            {!state.script && (
              <button type="button" className="btn small primary" onClick={() => void make()} disabled={busy}>
                {b ? 'Make a new bookmark' : 'Make the bookmark'}
              </button>
            )}
            {b && !!state.matched && (
              <button type="button" className="btn small" onClick={() => void rematch()}>
                Match the students again
              </button>
            )}
            {b && (
              <button type="button" className="btn small danger-text" onClick={() => void turnOff()}>
                Turn the bookmark off
              </button>
            )}
          </div>
          {b?.lastUsedAt ? (
            <p className="muted small">
              Last sent {when(b.lastUsedAt)} by {b.by}: {b.summary.map((s) => `${s.name} ${s.days} day${s.days === 1 ? '' : 's'}`).join(', ')}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
